import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../common/LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL;

// Each role's own landing dashboard (also used by AccessDeniedPage).
export const getRoleDashboardPath = (role) => {
  let dashboardPath = '/assessment_dashboard'; // default

  switch (role) {
    case 'CARA':
      dashboardPath = '/assessment_dashboard';
      break;
    case 'TPRM':
      dashboardPath = '/tprm_admin_dashboard';
      break;
    case 'TPRMUSER':
      dashboardPath = '/tprm_admin_dashboard';
      break;
    case 'VRM':
      dashboardPath = '/vrm_dashboard';
      break;
    case 'OFFSEC_STAFF':
      dashboardPath = '/offsec_staff_overview';
      break;
    case 'AUDIT':
      dashboardPath = '/audit_dashboard';
      break;
    case 'SUPERADMIN':
      dashboardPath = '/superadmin_dashboard';
      break;
    case 'INTERNALSTAFF':
      dashboardPath = '/superadmin_dashboard';
      break;
    default:
      dashboardPath = '/assessment_dashboard';
  }

  return dashboardPath;
};

const AuthGuard = ({ children }) => {
  // Initialize with cached state if available to prevent loading spinner
  const getInitialAuthState = () => {
    const authCache = localStorage.getItem('authCache');
    if (authCache) {
      try {
        const cachedState = JSON.parse(authCache);
        const now = Date.now();
        const AUTH_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

        // If cache is valid and recent, use it
        if (cachedState.isAuthenticated && (now - (cachedState.timestamp || 0)) < AUTH_CACHE_DURATION) {
          return {
            ...cachedState,
            isLoading: false
          };
        }
      } catch (e) {
        localStorage.removeItem('authCache');
      }
    }

    return {
      isLoading: true,
      isAuthenticated: false,
      userData: null,
      requiresOtp: false,
      error: null
    };
  };

  const [authState, setAuthState] = useState(getInitialAuthState);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [lastAuthCheck, setLastAuthCheck] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Routes that don't require authentication
  const publicRoutes = [
    '/login',
    '/cara_login',
    '/tprm_login',
    '/tprm_user_login',
    '/vrm_login',
    '/offsec_staff_login',
    '/superadmin_login',
    '/internal_staff_login',
    '/otp',
    '/ota',
    '/reset_password',
    '/set_password',
    '/expired-link',
    '/book_demo',
    '/404'
  ];

  // Check if current route is public
  const isPublicRoute = publicRoutes.some(route =>
    location.pathname === route || location.pathname.startsWith(route)
  );

  // Role-based redirect mapping
  const roleRedirectMap = {
    'CARA': '/cara_login',
    'TPRM': '/tprm_login',
    'TPRMUSER': '/tprm_user_login',
    'VRM': '/vrm_login',
    'OFFSEC_STAFF': '/offsec_staff_login',
    'AUDIT': '/login',
    'INTERNALSTAFF': '/internal_staff_login',
    'DEFAULT': '/login'
  };

  const checkAuthentication = async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

      console.log("AuthGuard: Checking authentication...");
      const response = await axios.get(`${API_URL}api/check-session/`, {
        withCredentials: true,
        timeout: 10000 // 10 second timeout
      });

      const { status, user_id, role } = response.data;

      if (status === 'active') {
        // User is fully authenticated
        const authData = {
          isLoading: false,
          isAuthenticated: true,
          userData: { user_id, role },
          requiresOtp: false,
          error: null,
          timestamp: Date.now()
        };
        setAuthState(authData);
        setHasCheckedAuth(true);
        setLastAuthCheck(Date.now());

        // Cache the authentication state
        localStorage.setItem('authCache', JSON.stringify(authData));

        // If user is on OTP page but already authenticated, redirect to appropriate dashboard
        if (location.pathname === '/otp' || location.pathname === '/ota') {
          redirectToDashboard(role);
        }
      } else if (status === 'pending_2fa') {
        // Password accepted but the Login Code is not verified yet. Only the
        // OTP pages may render in this state (CARAGRC-001) - every protected
        // route is sent back to the OTP step.
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          userData: { user_id, role },
          requiresOtp: true,
          error: null
        });
        setHasCheckedAuth(true);
        setLastAuthCheck(Date.now());
        localStorage.removeItem('authCache');

        if (!isPublicRoute) {
          navigate('/otp', { replace: true });
        }
      } else {
        // Session is inactive
        handleUnauthenticated();
      }
    } catch (error) {
      console.error('Authentication check failed:', error);

      // If it's a network error and we're on a public route, don't redirect
      if (isPublicRoute && (error.code === 'NETWORK_ERROR' || !error.response)) {
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          userData: null,
          requiresOtp: false,
          error: null
        });
        return;
      }

      // For any other error, treat as unauthenticated
      handleUnauthenticated();
    }
  };

  const handleUnauthenticated = () => {
    setAuthState({
      isLoading: false,
      isAuthenticated: false,
      userData: null,
      requiresOtp: false,
      error: null
    });
    setHasCheckedAuth(false);
    setLastAuthCheck(0);

    // Clear authentication cache
    localStorage.removeItem('authCache');

    // Only redirect if not on a public route
    if (!isPublicRoute) {
      const role = localStorage.getItem('role');
      const redirectPath = roleRedirectMap[role] || roleRedirectMap.DEFAULT;
      navigate(redirectPath);
    }
  };

  const redirectToDashboard = (role) => {
    navigate(getRoleDashboardPath(role));
  };

  useEffect(() => {
    // Skip authentication check for public routes
    if (isPublicRoute) {
      setAuthState({
        isLoading: false,
        isAuthenticated: false,
        userData: null,
        requiresOtp: false,
        error: null
      });
      setHasCheckedAuth(false);
      return;
    }

    // If user is already authenticated and we've checked recently, don't check again
    if (authState.isAuthenticated && hasCheckedAuth) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    // Check if we have a valid cached authentication state
    const now = Date.now();
    const authCache = localStorage.getItem('authCache');
    const AUTH_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes cache

    if (authCache) {
      try {
        const cachedState = JSON.parse(authCache);
        // If cache is valid and recent, render optimistically to avoid a loading
        // flicker, but the cache is unsigned client state and must never be
        // trusted on its own - always verify with the server in the background.
        if (cachedState.isAuthenticated && (now - lastAuthCheck) < AUTH_CACHE_DURATION) {
          setAuthState({
            ...cachedState,
            isLoading: false
          });
          setHasCheckedAuth(true);
          checkAuthentication();
          return;
        }
      } catch (e) {
        localStorage.removeItem('authCache');
      }
    }

    // Only check authentication if we haven't checked yet
    if (!hasCheckedAuth) {
      checkAuthentication();
    }
  }, [location.pathname, isPublicRoute]);

  // Listen for logout events to clear cache and redirect immediately
  useEffect(() => {
    const handleLogout = () => {
      console.log('🚨 AuthGuard: Logout event detected, clearing auth state');
      setAuthState({
        isLoading: false,
        isAuthenticated: false,
        userData: null,
        requiresOtp: false,
        error: null
      });
      setHasCheckedAuth(false);
      setLastAuthCheck(0);
      localStorage.removeItem('authCache');

      // If on a protected route, redirect immediately
      if (!isPublicRoute) {
        const role = localStorage.getItem('role');
        const redirectPath = roleRedirectMap[role] || roleRedirectMap.DEFAULT;
        navigate(redirectPath, { replace: true });
      }
    };

    const handleStorageChange = (e) => {
      // Listen for both old and new logout signals
      if ((e.key === 'logout' && e.newValue === 'true') ||
        (e.key === 'logout_immediate' && e.newValue)) {
        handleLogout();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLogout', handleLogout);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLogout', handleLogout);
    };
  }, [isPublicRoute, navigate]);

  // Show loading spinner only if we're actually checking authentication
  // Don't show if we have cached authentication data
  if (authState.isLoading && !authState.isAuthenticated) {
    return <LoadingSpinner message="Verifying your session..." />;
  }

  // For public routes, always render children
  if (isPublicRoute) {
    return children;
  }

  // Protected routes render only for a fully authenticated (MFA-complete)
  // session; a pending-OTP session is redirected to /otp above.
  if (authState.isAuthenticated) {
    return children;
  }

  // If not authenticated and not on public route, show loading while redirecting
  return <LoadingSpinner message="Redirecting to login..." />;
};

export default AuthGuard;
